import { useEffect, useState } from "react";
import { CONTENT } from "@/content";
import { BorderBeam } from "@/components/ui/border-beam";
import { apiFetch, ApiError } from "@/lib/api";
import { funnelState } from "@/lib/funnelState";
import { StepBar } from "./StepBar";

type SlotDto = {
  id: number;
  data: string;        // YYYY-MM-DD
  dia_semana: string;  // "terca", "quarta"…
  inicio: string;      // HH:MM:SS
  fim: string;
};

type Props = {
  onConfirm: () => void;
  onHome: () => void;
};

const DOW_LABEL: Record<string, string> = {
  domingo: "Domingo",
  segunda: "Segunda",
  terca: "Terça",
  quarta: "Quarta",
  quinta: "Quinta",
  sexta: "Sexta",
  sabado: "Sábado",
};

function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}`;
}

function formatTime(t: string): string {
  // "09:00:00" → "9h"; "07:30:00" → "7h30"
  const [hh, mm] = t.split(":");
  if (!hh) return t;
  const h = Number(hh);
  if (!mm || mm === "00") return `${h}h`;
  return `${h}h${mm}`;
}

function formatRange(s: SlotDto): string {
  return `${formatTime(s.inicio)} às ${formatTime(s.fim)}`;
}

export function P4({ onConfirm, onHome }: Props) {
  const p = CONTENT.p4;
  const [slots, setSlots] = useState<SlotDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(funnelState.getSlotId());
  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch<{ slots: SlotDto[] }>("/slots")
      .then((res) => {
        if (cancelled) return;
        setSlots(res.slots);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Não conseguimos carregar os horários. Tente novamente.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const doConfirm = async () => {
    const leadId = funnelState.getLeadId();
    if (!leadId || selectedId == null || submitting) return;
    setSubmitting(true);
    try {
      await apiFetch(`/leads/${leadId}/booking`, {
        method: "POST",
        body: { slot_id: selectedId },
        auth: "lead",
      });
      funnelState.setSlotId(selectedId);
      setConfirmed(true);
      setTimeout(() => onConfirm(), 3600);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? "Esse horário acabou de ficar cheio. Escolha outro, por favor."
          : "Não conseguimos confirmar agora. Tente novamente.",
      );
      setSubmitting(false);
    }
  };

  if (confirmed) {
    const s = slots.find((x) => x.id === selectedId);
    return (
      <div className="fx page-enter">
        <StepBar step={2} onHome={onHome} />
        <main className="fx-main fx-main-center">
          <div className="fx-confirm">
            <span className="fx-check">
              <span className="fx-check-mark">
                <svg className="fx-check-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M5 12.5l4.2 4.2L19 7" />
                </svg>
              </span>
            </span>
            <h1 className="fx-confirm-title">{p.confirmTitle}</h1>
            {s && (
              <p className="fx-confirm-slot">
                {formatDate(s.data)} · {DOW_LABEL[s.dia_semana] ?? s.dia_semana} · {formatRange(s)}
              </p>
            )}
            <p className="fx-confirm-text">{p.confirmText}</p>
            <p className="fx-confirm-final">{p.confirmFinal}</p>
            <span className="fx-loadbar">
              <i />
            </span>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="fx page-enter">
      <StepBar step={2} onHome={onHome} />
      <main className="fx-main">
        <div className="fx-col">
          <span className="fx-kicker">Avaliação Diagnóstica</span>
          <h1 className="fx-h">{p.title}</h1>
          <p className="fx-open">{p.open}</p>

          <div className="fx-sched">
            <div className="fx-panel">
              <h3 className="fx-panel-t">{p.whatTitle}</h3>
              <p className="fx-panel-x">{p.whatText}</p>
              <ul className="fx-skills">
                {p.skills.map(([en, pt], i) => (
                  <li key={i}>
                    <span className="fx-skill-en">{en}</span>
                    <span className="fx-skill-pt">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="fx-panel">
              <h3 className="fx-panel-t">{p.deliverTitle}</h3>
              <ul className="fx-deliver">
                {p.deliver.map((d, i) => (
                  <li key={i}>
                    <span className="fx-deliver-c">✓</span>
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="fx-slots-head">
            <h3 className="fx-panel-t">{p.slotsTitle}</h3>
            {!loading && slots.length > 0 && (
              <span className="fx-window">
                {formatDate(slots[0]!.data)} a {formatDate(slots[slots.length - 1]!.data)}
              </span>
            )}
          </div>

          {loading && <p className="fx-explain">Carregando horários…</p>}
          {!loading && slots.length === 0 && (
            <p className="fx-explain">
              Não há horários disponíveis no momento. Fale conosco pelo WhatsApp{" "}
              {CONTENT.brand.whatsapp} para encaixe.
            </p>
          )}

          <div className="fx-slots">
            {slots.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`fx-slot ${selectedId === s.id ? "on" : ""}`}
                onClick={() => setSelectedId(s.id)}
              >
                <span className="fx-slot-date">{formatDate(s.data)}</span>
                <span className="fx-slot-day">{DOW_LABEL[s.dia_semana] ?? s.dia_semana}</span>
                <span className="fx-slot-time">{formatRange(s)}</span>
                <span className="fx-slot-radio" />
              </button>
            ))}
          </div>

          {error && <div className="adm-login-error" style={{ marginBottom: 16 }}>{error}</div>}

          <div className="fx-actions">
            <button
              type="button"
              className={`btn btn-primary btn-lg btn-beam btn-beam-primary ${selectedId != null && !submitting ? "" : "is-wait"}`}
              onClick={doConfirm}
              disabled={selectedId == null || submitting}
            >
              <BorderBeam />
              {submitting ? "Confirmando…" : p.button} <span className="arr">→</span>
            </button>
          </div>
          <p className="fx-after">{p.after}</p>
        </div>
      </main>
    </div>
  );
}
